import { useEffect } from "react";
import { api, getToken } from "./api";
import { enableWebPush } from "./pwa";

const POLL_MS = 30_000;
const PREF_KEY = "assignoo.browser-notify.enabled";

export function notificationsEnabled() { return localStorage.getItem(PREF_KEY) === "1"; }
export function setNotificationsEnabled(v) { localStorage.setItem(PREF_KEY, v ? "1" : "0"); }

export function browserNotificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

/** Must be called from a user gesture. Returns "granted" | "denied" | "unsupported". */
export async function requestNotificationPermission() {
  if (!browserNotificationsSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  try {
    const res = await Notification.requestPermission();
    return res === "granted" ? "granted" : "denied";
  } catch {
    return "denied";
  }
}

async function showSystemNotification(title, body, url) {
  try {
    if (navigator.serviceWorker?.controller) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, { body, icon: "/branding/icon-192.png", badge: "/branding/icon-192.png", data: { url }, tag: url || "assignoo" });
      return;
    }
  } catch { /* fall back to page-level notification */ }
  try {
    const n = new Notification(title, { body, icon: "/branding/icon-192.png", tag: url || "assignoo" });
    n.onclick = () => { window.focus(); if (url) window.location.assign(url); n.close(); };
  } catch { /* noop */ }
}

/**
 * Browser alerts while Assignoo is open (background tabs included): polls
 * /notifications and fires a system notification for each new unread item.
 * When the relay VAPID key is configured, also registers a true web-push
 * subscription so alerts keep arriving with the tab closed.
 */
export function useBrowserNotifications({ userId }) {
  useEffect(() => {
    if (!userId || !getToken()) return undefined;
    if (!notificationsEnabled() || Notification.permission !== "granted") return undefined;
    let timer = null;
    let last = null; // latest created_at seen (null => prime on first poll)

    const poll = async () => {
      try {
        const res = await api("/notifications", { query: { page: 1, page_size: 15 } });
        const items = res?.items || [];
        if (!items.length) return;
        if (last === null) { last = items[0].created_at; return; }
        const fresh = items.filter((i) => !i.read && i.created_at > last);
        if (fresh.length) {
          last = items[0].created_at;
          for (const n of fresh.slice(0, 3).reverse()) {
            const url = n.task_id ? `/task/${n.task_id}` : "/notifications";
            await showSystemNotification(n.title || "Assignoo", n.body || "", url);
          }
        }
      } catch { /* best-effort */ }
    };

    enableWebPush().catch(() => { /* relay key not configured yet */ });
    poll();
    timer = setInterval(() => { if (document.visibilityState !== "hidden") poll(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (timer) clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [userId]);
}
